import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, Panel } from "@akaike/ui";
import { IconLock } from "../components/icons";
import { useDocumentTitle } from "../lib/document-title";
import { signOut, useSession } from "./client";
import { CurrentUserContext } from "./me";
import { meV1MeGet } from "../api/generated/qcsControlPlane";
import { ApiError } from "../api/mutator";
import { Login } from "./Login";

function Centered({ children }: { children: ReactNode }) {
  return <div className="center-screen">{children}</div>;
}

function Loading({ label }: { label: string }) {
  return (
    <Centered>
      <div className="spinner" />
      <div className="muted">{label}</div>
    </Centered>
  );
}

function NotAuthorized({ email }: { email?: string }) {
  useDocumentTitle("Not authorized");
  return (
    <Centered>
      <Panel style={{ maxWidth: 460, padding: "28px 26px", textAlign: "center" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: 14,
            color: "var(--brand)",
          }}
        >
          <span style={{ width: 34, height: 34, display: "inline-block" }}>
            <IconLock />
          </span>
        </div>
        <h2 style={{ fontSize: 22, marginBottom: 10 }}>Not authorized</h2>
        <p className="muted" style={{ marginBottom: 20, lineHeight: 1.5 }}>
          {email ? <b style={{ color: "var(--ink)" }}>{email}</b> : "Your account"} isn’t provisioned
          for this console: ask an administrator to invite you.
        </p>
        <Button variant="outline" onClick={() => signOut()}>
          Sign out
        </Button>
      </Panel>
    </Centered>
  );
}

function ApiUnreachable() {
  useDocumentTitle("API unreachable");
  return (
    <Centered>
      <Panel alarm style={{ maxWidth: 460, padding: "26px", textAlign: "center" }}>
        <h2 style={{ fontSize: 20, marginBottom: 10 }}>API unreachable</h2>
        <p className="muted" style={{ marginBottom: 18 }}>
          Signed in, but the control plane didn’t answer. Is it up?
        </p>
        <Button variant="outline" onClick={() => signOut()}>
          Sign out
        </Button>
      </Panel>
    </Centered>
  );
}

// Gate the whole app: require a Better Auth session AND a provisioned, active
// control-plane user. A 403 from /v1/me is the "ask an administrator" case.
export function RequireAuth({ children }: { children: ReactNode }) {
  const { data: session, isPending } = useSession();

  const meQuery = useQuery({
    queryKey: ["me"],
    enabled: !!session,
    retry: false,
    queryFn: () => meV1MeGet(),
  });

  if (isPending) return <Loading label="Checking session…" />;
  if (!session) return <Login />;
  if (meQuery.isLoading) return <Loading label="Loading profile…" />;
  if (meQuery.isError) {
    const err = meQuery.error;
    if (err instanceof ApiError && err.status === 403) {
      return <NotAuthorized email={session.user.email} />;
    }
    return <ApiUnreachable />;
  }
  if (!meQuery.data) return <ApiUnreachable />;

  return <CurrentUserContext.Provider value={meQuery.data}>{children}</CurrentUserContext.Provider>;
}
